import type { TokenStore } from "@/domains/tokens/token-store"
import { concentratedApr, priceToTick } from "@/domains/manual-cl-core"

/**
 * Newest-first-compatible day/hour aggregate shape. Mirrors the fields
 * `ManualClPoolStore.poolDayData` already exposes from the indexer; kept
 * structural so this helper stays free of thick-v2 store imports.
 */
export interface PoolIntervalDatum {
  high?: number
  low?: number
  close?: number
  open?: number
  volumeUSD: number
  feesUSD?: number
}

export interface SevenDayRangeAprInputs {
  token0: TokenStore
  token1: TokenStore
  /** Current on-chain tick. */
  currentTick: number | undefined
  /**
   * Current price in canonical `token1 / token0` orientation. Used to
   * reconcile against `poolDayData` close values so we flip OHLC prices
   * into canonical orientation before converting them to ticks. The same
   * approach the price-history composite already uses to draw the dashed
   * reference lines.
   */
  currentPrice: number | undefined
  /** Uniswap fee tier in hundredths of a bip (500 | 3000 | 10000). */
  feeTier: number
  /** Pool TVL in USD — denominator of the baseline pool APR. */
  tvlUsd: number
  /**
   * Newest-first daily aggregates (up to seven). Must carry `high` /
   * `low` / `volumeUSD` fields; missing `high` / `low` fall back to
   * `close` so pools that only emit close prices still get a usable range.
   */
  poolDayData: ReadonlyArray<PoolIntervalDatum>
  /**
   * Tick spacing for the pool's fee tier. When provided the derived 7D
   * high/low ticks snap to spacing (purely cosmetic; doesn't change the
   * APR enough to matter but keeps the returned tick pair consistent
   * with what the create flow would present as the strategy boundaries).
   * Defaults to `1` (no snap).
   */
  tickSpacing?: number
  /**
   * Symmetric fallback range in fractional form (e.g. `0.02` for ±2%).
   * Only used when `poolDayData` is unusable — no entries, no positive
   * high/low, or the derived range collapses to a single tick. Defaults
   * to `0.02` (±2%).
   */
  fallbackPct?: number
}

export interface SevenDayRangeAprResult {
  /**
   * Range-aware APR (%). Represents the return a position sized at the
   * 7-day high/low would have earned, annualised from the actual 7-day
   * volume. `0` when we cannot produce a meaningful number.
   */
  aprPct: number
  /**
   * Same pool "headline" APR as `ManualClPoolStore.displayApr`, but
   * computed against the same 7-day volume window for an honest
   * apples-to-apples comparison with `aprPct` (vs 24h noise).
   */
  baselineAprPct: number
  /** Capital-efficiency multiplier implied by the derived range. */
  capitalEfficiency: number
  /** Lower tick of the derived range. */
  tickLower: number | undefined
  /** Upper tick of the derived range. */
  tickUpper: number | undefined
  /**
   * Which path produced the returned range — used by the UI to label whether
   * the price range came from observed highs/lows or a current-price fallback.
   */
  source: "seven-day-history" | "three-day-history" | "twenty-four-hour-history" | "fallback-pct" | "unavailable"
  /** Which volume window fed the APR annualisation. */
  sourceWindow: "7d" | "3d" | "24h" | "none"
  /** Human-readable source label for tooltips. */
  sourceLabel: string
  /** Whether the unavailable state means the pair lacks indexed USD metrics. */
  dataStatus: "ready" | "unindexed-pair" | "unavailable"
  /** Whether `currentTick` falls inside `[tickLower, tickUpper)`. */
  inRange: boolean
  /** Formatted value for direct UI consumption. Empty string → render `—`. */
  formattedPct: string
  /** Formatted baseline for tooltip rendering. Empty string → omit. */
  formattedBaselinePct: string
}

const DEFAULT_FALLBACK_PCT = 0.02

const UNAVAILABLE: SevenDayRangeAprResult = {
  aprPct: 0,
  baselineAprPct: 0,
  capitalEfficiency: 0,
  tickLower: undefined,
  tickUpper: undefined,
  source: "unavailable",
  sourceWindow: "none",
  sourceLabel: "Not enough data to compute a range APR",
  dataStatus: "unavailable",
  inRange: false,
  formattedPct: "",
  formattedBaselinePct: "",
}

/**
 * Compute the "7-day range APR" we surface on the Manual-CL pool list —
 * the annualised yield a marginal LP would have earned over the last
 * seven days if they had sized their position exactly to the 7-day
 * high/low, rather than a full-range LP.
 *
 * Pipeline:
 *
 *   1. Reconcile orientation — OHLC high/low from the indexer may sit
 *      in either `token0/token1` or `token1/token0` depending on the
 *      pool; we flip into canonical `token1/token0` by comparing close
 *      median to `currentPrice`.
 *   2. Convert the canonical-orientation prices to ticks. No snapping
 *      by default — the result feeds a CE calculation which is
 *      smooth over ticks.
 *   3. Select the strongest available positive APR from 7D daily, 3D daily,
 *      and today's UTC daily volume. Annualise by the actual data window.
 *   4. `rangeAprPct = poolBaselineApr × capitalEfficiency(range)` — the
 *      identity that holds in the infinitesimal-size limit.
 *
 * Falls back to a symmetric ±`fallbackPct` range when history is incomplete
 * or the observed high/low sits outside the current price. The fallback is
 * honest: the returned `source` and `sourceLabel` tell the UI which path won.
 */
export function sevenDayRangeApr(args: SevenDayRangeAprInputs): SevenDayRangeAprResult {
  const {
    token0,
    token1,
    currentTick,
    currentPrice,
    feeTier,
    tvlUsd,
    poolDayData,
    tickSpacing = 1,
    fallbackPct = DEFAULT_FALLBACK_PCT,
  } = args

  if (
    currentTick === undefined ||
    !Number.isFinite(currentTick) ||
    !tvlUsd ||
    !Number.isFinite(tvlUsd) ||
    tvlUsd <= 0 ||
    !feeTier ||
    feeTier <= 0
  ) {
    return UNAVAILABLE
  }

  const feeRate = feeTier / 1_000_000
  const candidates = [
    poolDayData.length >= 7
      ? computeRangeAprCandidate({
          token0,
          token1,
          currentTick,
          currentPrice,
          feeTier,
          feeRate,
          tvlUsd,
          rows: poolDayData.slice(0, 7),
          windowDays: 7,
          source: "seven-day-history",
          sourceWindow: "7d",
          historyLabel: "Based on the pool's 7-day high/low",
          fallbackLabel: "7D volume with a current ±2% fallback range",
          tickSpacing,
          fallbackPct,
        })
      : undefined,
    poolDayData.length >= 2
      ? computeRangeAprCandidate({
          token0,
          token1,
          currentTick,
          currentPrice,
          feeTier,
          feeRate,
          tvlUsd,
          rows: poolDayData.slice(0, 3),
          windowDays: Math.min(poolDayData.length, 3),
          source: "three-day-history",
          sourceWindow: "3d",
          historyLabel: "Based on the pool's recent daily high/low",
          fallbackLabel: "Recent daily volume with a current ±2% fallback range",
          tickSpacing,
          fallbackPct,
        })
      : undefined,
    poolDayData.length === 1
      ? computeRangeAprCandidate({
          token0,
          token1,
          currentTick,
          currentPrice,
          feeTier,
          feeRate,
          tvlUsd,
          rows: poolDayData.slice(0, 1),
          windowDays: 1,
          source: "twenty-four-hour-history",
          sourceWindow: "24h",
          historyLabel: "Based on today's pool high/low",
          fallbackLabel: "Today volume with a current ±2% fallback range",
          tickSpacing,
          fallbackPct,
        })
      : undefined,
  ].filter((candidate): candidate is SevenDayRangeAprResult => candidate !== undefined)

  const strongest = candidates.reduce<SevenDayRangeAprResult | undefined>((best, candidate) => {
    if (!candidate.formattedPct || candidate.aprPct <= 0) return best
    if (!best || candidate.aprPct > best.aprPct) return candidate
    return best
  }, undefined)

  if (strongest) return strongest

  return {
    ...UNAVAILABLE,
    dataStatus: "unindexed-pair",
    sourceLabel: "No pricing for USD APR metrics",
  }
}

function computeRangeAprCandidate(args: {
  token0: TokenStore
  token1: TokenStore
  currentTick: number
  currentPrice: number | undefined
  feeTier: number
  feeRate: number
  tvlUsd: number
  rows: ReadonlyArray<PoolIntervalDatum>
  windowDays: number
  source: "seven-day-history" | "three-day-history" | "twenty-four-hour-history"
  sourceWindow: "7d" | "3d" | "24h"
  historyLabel: string
  fallbackLabel: string
  tickSpacing: number
  fallbackPct: number
}): SevenDayRangeAprResult | undefined {
  const volumeUsd = sumFiniteVolume(args.rows)
  if (volumeUsd <= 0 || args.windowDays <= 0) return undefined

  const baselineAprPct = ((volumeUsd * args.feeRate * (365 / args.windowDays)) / args.tvlUsd) * 100
  if (!Number.isFinite(baselineAprPct) || baselineAprPct <= 0) return undefined

  const historyRange = deriveHistoryTickRange({
    token0: args.token0,
    token1: args.token1,
    currentPrice: args.currentPrice,
    poolDayData: args.rows,
    tickSpacing: args.tickSpacing,
    source: args.source,
  })
  const historyInRange =
    historyRange && historyRange.tickLower <= args.currentTick && args.currentTick < historyRange.tickUpper
  const rangeTicks = historyInRange
    ? historyRange
    : deriveFallbackTickRange({
        currentTick: args.currentTick,
        fallbackPct: args.fallbackPct,
        tickSpacing: args.tickSpacing,
      })

  if (!rangeTicks) {
    return {
      ...UNAVAILABLE,
      baselineAprPct,
      formattedBaselinePct: formatAprPct(baselineAprPct),
      sourceWindow: args.sourceWindow,
    }
  }

  const probe = concentratedApr({
    currentTick: args.currentTick,
    feeTierBps: args.feeTier,
    volumeUsd24h: 0,
    totalActiveLiquidity: 0n,
    tickLower: rangeTicks.tickLower,
    tickUpper: rangeTicks.tickUpper,
    positionLiquidity: 0n,
    positionValueUsd: 0,
    positionAlreadyInPool: false,
  })
  const capitalEfficiency = probe.capitalEfficiency
  const inRange = rangeTicks.tickLower <= args.currentTick && args.currentTick < rangeTicks.tickUpper
  if (!inRange || !(capitalEfficiency > 0) || !Number.isFinite(capitalEfficiency)) {
    return undefined
  }

  const aprPct = baselineAprPct * capitalEfficiency
  return {
    aprPct: Number.isFinite(aprPct) && aprPct > 0 ? aprPct : 0,
    baselineAprPct,
    capitalEfficiency,
    tickLower: rangeTicks.tickLower,
    tickUpper: rangeTicks.tickUpper,
    source: rangeTicks.source === "fallback-pct" ? "fallback-pct" : args.source,
    sourceWindow: args.sourceWindow,
    sourceLabel: rangeTicks.source === "fallback-pct" ? args.fallbackLabel : args.historyLabel,
    dataStatus: "ready",
    inRange: true,
    formattedPct: formatAprPct(aprPct),
    formattedBaselinePct: formatAprPct(baselineAprPct),
  }
}

function sumFiniteVolume(data: ReadonlyArray<PoolIntervalDatum>): number {
  let total = 0
  for (const row of data) {
    const volume = row?.volumeUSD
    if (Number.isFinite(volume) && volume > 0) {
      total += volume
    }
  }
  return total
}

/**
 * Turn OHLC high/low into a pair of canonically-oriented ticks. Returns
 * `undefined` when the day data produces a degenerate range — callers
 * should fall back to `deriveFallbackTickRange`.
 */
function deriveHistoryTickRange(args: {
  token0: TokenStore
  token1: TokenStore
  currentPrice: number | undefined
  poolDayData: ReadonlyArray<PoolIntervalDatum>
  tickSpacing: number
  source: "seven-day-history" | "three-day-history" | "twenty-four-hour-history"
}):
  | {
      tickLower: number
      tickUpper: number
      source: "seven-day-history" | "three-day-history" | "twenty-four-hour-history"
    }
  | undefined {
  const { token0, token1, currentPrice, poolDayData, tickSpacing, source } = args

  let low = Infinity
  let high = -Infinity
  const closeSamples: Array<number> = []
  for (const row of poolDayData) {
    const lowCandidate = row?.low ?? row?.close
    const highCandidate = row?.high ?? row?.close
    if (Number.isFinite(lowCandidate) && (lowCandidate as number) > 0) {
      if ((lowCandidate as number) < low) low = lowCandidate as number
    }
    if (Number.isFinite(highCandidate) && (highCandidate as number) > 0) {
      if ((highCandidate as number) > high) high = highCandidate as number
    }
    const closeCandidate = row?.close ?? row?.open
    if (Number.isFinite(closeCandidate) && (closeCandidate as number) > 0) {
      closeSamples.push(closeCandidate as number)
    }
  }

  if (!Number.isFinite(low) || !Number.isFinite(high) || low <= 0 || high <= 0 || low >= high) {
    return undefined
  }

  const needsInvert = shouldInvertForCanonical({
    closeSamples,
    referenceValue: currentPrice,
  })
  const lowPrice = needsInvert ? 1 / high : low
  const highPrice = needsInvert ? 1 / low : high
  if (lowPrice >= highPrice) return undefined

  const lowTick = safePriceToTick({ price: lowPrice, token0, token1, tickSpacing })
  const highTick = safePriceToTick({ price: highPrice, token0, token1, tickSpacing })
  if (lowTick === undefined || highTick === undefined) return undefined
  const tickLower = Math.min(lowTick, highTick)
  const tickUpper = Math.max(lowTick, highTick)
  if (tickUpper - tickLower < Math.max(tickSpacing, 1)) {
    // Degenerate range (history collapsed to a single tick) — the
    // caller will fall back.
    return undefined
  }

  return { tickLower, tickUpper, source }
}

function deriveFallbackTickRange(args: {
  currentTick: number
  fallbackPct: number
  tickSpacing: number
}): { tickLower: number; tickUpper: number; source: "fallback-pct" } | undefined {
  const { currentTick, fallbackPct, tickSpacing } = args
  if (!Number.isFinite(fallbackPct) || fallbackPct <= 0 || fallbackPct >= 1) {
    return undefined
  }
  const deltaTicks = Math.max(tickSpacing, Math.round(Math.log(1 + fallbackPct) / Math.log(1.0001)))
  const snappedLower = snapDown(currentTick - deltaTicks, tickSpacing)
  const snappedUpper = snapUp(currentTick + deltaTicks, tickSpacing)
  if (snappedUpper - snappedLower < Math.max(tickSpacing, 1)) return undefined
  return { tickLower: snappedLower, tickUpper: snappedUpper, source: "fallback-pct" }
}

function safePriceToTick(args: {
  price: number
  token0: TokenStore
  token1: TokenStore
  tickSpacing: number
}): number | undefined {
  try {
    return priceToTick({
      price: String(args.price),
      token0: args.token0,
      token1: args.token1,
      tickSpacing: args.tickSpacing,
    })
  } catch {
    return undefined
  }
}

function shouldInvertForCanonical(args: {
  closeSamples: ReadonlyArray<number>
  referenceValue: number | undefined
}): boolean {
  const { closeSamples, referenceValue } = args
  if (
    referenceValue === undefined ||
    !Number.isFinite(referenceValue) ||
    referenceValue <= 0 ||
    closeSamples.length === 0
  ) {
    return false
  }
  const median = medianOf(closeSamples)
  if (median === undefined || median <= 0) return false
  const distanceAsIs = Math.abs(Math.log10(referenceValue) - Math.log10(median))
  const distanceInverted = Math.abs(Math.log10(1 / referenceValue) - Math.log10(median))
  return distanceInverted < distanceAsIs
}

function medianOf(values: ReadonlyArray<number>): number | undefined {
  if (values.length === 0) return undefined
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 0) {
    const low = sorted[mid - 1]
    const high = sorted[mid]
    if (low === undefined || high === undefined) return undefined
    return (low + high) / 2
  }
  return sorted[mid]
}

function snapDown(tick: number, spacing: number): number {
  if (spacing <= 1) return Math.floor(tick)
  return Math.floor(tick / spacing) * spacing
}

function snapUp(tick: number, spacing: number): number {
  if (spacing <= 1) return Math.ceil(tick)
  return Math.ceil(tick / spacing) * spacing
}

function formatAprPct(aprPct: number): string {
  if (!Number.isFinite(aprPct) || aprPct <= 0) return ""
  if (aprPct < 0.01) return "<0.01%"
  return `${aprPct.toFixed(2)}%`
}
